/**
 * History Page - Draft History (EPIC 9)
 * 
 * Displays all proposal drafts for the team with:
 * - Search and filtering
 * - Status filter tabs (All/Draft/Sent/Discarded)
 * - Pagination
 * - Token/cost breakdown (including analyses)
 * - Quick actions (copy, delete)
 * 
 * Per design.md v0.4:
 * - Uses semantic tokens
 * - lucide icons only
 * - BlurFade animations
 * - Uses @uprosal/ui components
 */

import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../../convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
    Badge, Button, Skeleton, Input,
    History, Search, Clock, FileText, Trash2, Copy, Check, Coins, ExternalLink, Zap,
} from '@uprosal/ui';
import { BlurFade } from '../../components/BlurFade';
import { ConfirmDialog } from '../../components/ConfirmDialog';

type StatusFilter = 'all' | 'draft' | 'sent' | 'discarded';
type DraftListItem = {
    _id: Id<'proposalDrafts'>;
    jobId: Id<'jobs'>;
    jobTitle: string;
    jobUrl?: string;
    draftText: string;
    tokensUsed?: number;
    tokensIn?: number;
    tokensOut?: number;
    model?: string;
    estimatedCost?: number;
    status?: string;
    tone?: string;
    createdAt: number;
    templateName: string;
};
type DraftsResult = {
    items: DraftListItem[];
    nextCursor: number | null;
    hasMore: boolean;
};

export function HistoryPage() {
    const [cursor, setCursor] = useState<number | undefined>(undefined);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [copied, setCopied] = useState<Id<'proposalDrafts'> | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Id<'proposalDrafts'> | null>(null);

    // Fetch recent drafts
    const draftsResult = useQuery(api.queries.drafts.listRecent, {
        cursor: cursor || undefined,
        limit: 20,
        status: statusFilter === 'all' ? undefined : statusFilter,
    }) as DraftsResult | undefined;

    // Fetch analysis stats
    const analysisStats = useQuery(api.queries.analyses.getAnalysisStats, {});

    const deleteDraft = useMutation(api.mutations.proposalDrafts.deleteDraft);

    const isLoading = draftsResult === undefined;
    const drafts = (draftsResult?.items ?? []) as DraftListItem[];
    const hasMore = draftsResult?.hasMore ?? false;

    const handleCopy = async (text: string, id: Id<'proposalDrafts'>) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(id);
            setTimeout(() => setCopied(null), 2000);
        } catch (e) {
            console.error('Failed to copy:', e);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await deleteDraft({ draftId: deleteTarget });
        } catch (e) {
            console.error('Failed to delete:', e);
        } finally {
            setDeleteTarget(null);
        }
    };

    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now.getTime() - timestamp;

        if (diff < 86400000) { // < 24 hours
            const hours = Math.floor(diff / 3600000);
            if (hours < 1) return 'Just now';
            return `${hours}h ago`;
        }
        if (diff < 604800000) { // < 7 days
            const days = Math.floor(diff / 86400000);
            return `${days}d ago`;
        }
        return date.toLocaleDateString();
    };

    const formatCost = (cost?: number) => {
        if (!cost) return '-';
        return `$${cost.toFixed(4)}`;
    };

    const filteredDrafts = drafts.filter(draft =>
        !searchQuery ||
        draft.draftText.toLowerCase().includes(searchQuery.toLowerCase()) ||
        draft.jobTitle.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Stats calculations
    const draftTokens = drafts.reduce((sum: number, d) => sum + (d.tokensUsed || 0), 0);
    const draftCost = drafts.reduce((sum: number, d) => sum + (d.estimatedCost || 0), 0);
    const analysisTokens = analysisStats?.totalTokens ?? 0;
    const analysisCost = analysisStats?.estimatedCost ?? 0;
    const totalTokens = draftTokens + analysisTokens;
    const totalCost = draftCost + analysisCost;

    const statusFilters: { value: StatusFilter; label: string }[] = [
        { value: 'all', label: 'All' },
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'discarded', label: 'Discarded' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <BlurFade delay={0}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <History className="w-6 h-6" />
                            Draft History
                        </h1>
                        <p className="text-muted-foreground">
                            View and manage your proposal drafts
                        </p>
                    </div>
                </div>
            </BlurFade>

            {/* Search + Status Filters */}
            <BlurFade delay={0.1}>
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative max-w-md flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Search drafts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                        {statusFilters.map((filter) => (
                            <Button
                                key={filter.value}
                                variant={statusFilter === filter.value ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => {
                                    setStatusFilter(filter.value);
                                    setCursor(undefined); // Reset pagination
                                }}
                                className="text-sm"
                            >
                                {filter.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </BlurFade>

            {/* Stats Row */}
            <BlurFade delay={0.15}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{drafts.length}</p>
                                    <p className="text-sm text-muted-foreground">Total Drafts</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Zap className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{analysisStats?.totalAnalyses ?? 0}</p>
                                    <p className="text-sm text-muted-foreground">Job Analyses</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Coins className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        {(totalTokens / 1000).toFixed(1)}K
                                    </p>
                                    <p className="text-sm text-muted-foreground">Tokens Used</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Coins className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        ${totalCost.toFixed(4)}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Total Cost</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </BlurFade>

            {/* Drafts List */}
            <BlurFade delay={0.2}>
                <Card>
                    <CardHeader>
                        <CardTitle>Recent Drafts</CardTitle>
                        <CardDescription>
                            Your most recently generated proposal drafts
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <div className="space-y-4">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex gap-4 p-4 border border-border rounded-lg">
                                        <Skeleton className="w-full h-20" />
                                    </div>
                                ))}
                            </div>
                        ) : filteredDrafts.length === 0 ? (
                            <div className="text-center py-12">
                                <History className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                                <p className="text-muted-foreground">
                                    {searchQuery ? 'No drafts match your search' : 'No drafts yet'}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Generate proposals in the Chrome extension to see them here
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredDrafts.map((draft) => (
                                    <div
                                        key={draft._id}
                                        className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                {/* Job title */}
                                                <h3 className="font-medium truncate mb-1">
                                                    {draft.jobTitle || 'Untitled Job'}
                                                </h3>
                                                {/* Draft preview */}
                                                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                                    {draft.draftText.substring(0, 200)}...
                                                </p>
                                                {/* Metadata row */}
                                                <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {formatDate(draft.createdAt)}
                                                    </span>
                                                    {draft.templateName && draft.templateName !== 'Default' && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            {draft.templateName}
                                                        </Badge>
                                                    )}
                                                    {draft.tokensUsed && (
                                                        <span className="flex items-center gap-1">
                                                            <Coins className="w-3 h-3" />
                                                            {(draft.tokensUsed / 1000).toFixed(1)}K tokens
                                                        </span>
                                                    )}
                                                    {draft.estimatedCost && (
                                                        <span>{formatCost(draft.estimatedCost)}</span>
                                                    )}
                                                    {draft.status && (
                                                        <Badge
                                                            variant={draft.status === 'sent' ? 'success' : 'outline'}
                                                            className="text-xs"
                                                        >
                                                            {draft.status}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Actions */}
                                            <div className="flex items-center gap-2 shrink-0">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleCopy(draft.draftText, draft._id)}
                                                    title="Copy to clipboard"
                                                >
                                                    {copied === draft._id ? (
                                                        <Check className="w-4 h-4 text-primary" />
                                                    ) : (
                                                        <Copy className="w-4 h-4" />
                                                    )}
                                                </Button>
                                                {draft.jobUrl && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        asChild
                                                        title="Open job"
                                                    >
                                                        <a href={draft.jobUrl} target="_blank" rel="noopener noreferrer">
                                                            <ExternalLink className="w-4 h-4" />
                                                        </a>
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setDeleteTarget(draft._id)}
                                                    title="Delete draft"
                                                    className="text-destructive hover:text-destructive"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Load more */}
                        {hasMore && !isLoading && (
                            <div className="mt-4 text-center">
                                <Button
                                    variant="outline"
                                    onClick={() => setCursor(draftsResult?.nextCursor ?? undefined)}
                                >
                                    Load More
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </BlurFade>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                title="Delete Draft"
                description="Are you sure you want to delete this draft? This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
            />
        </div>
    );
}
